import { BoxProps, Flex } from '@chakra-ui/react';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, Editor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect } from 'react';

import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

import './richTextEditor.scss';

const Toolbar = ({ editor }: { editor: Editor }) => {
  const mode = useSimpleColorMode();
  return (
    <Flex
      display="flex"
      gap="0.25rem"
      padding="0.25rem"
      flexWrap="wrap"
      background={mode('gray.50', 'gray.900')}
      borderBottomColor={mode('gray.200', 'gray.800')}
      borderBottomWidth="1px"
      borderTopLeftRadius="0.375rem"
      borderTopRightRadius="0.375rem"
    >
      <IconButtonV2
        icon={<Icon icon="b" />}
        aria-label="Bold"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        isDisabled={!editor.can().chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
      />
      <IconButtonV2
        icon={<Icon icon="italic" />}
        aria-label="Italic"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isDisabled={!editor.can().chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <IconButtonV2
        icon={<Icon icon="strikethrough" />}
        aria-label="Strikethrough"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isDisabled={!editor.can().chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <IconButtonV2
        icon={<Icon icon="code" />}
        aria-label="Code"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isDisabled={!editor.can().chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <IconButtonV2
        icon={<Icon icon="list" />}
        aria-label="Bullet list"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <IconButtonV2
        icon={<Icon icon="list-ol" />}
        aria-label="Ordered list"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <IconButtonV2
        icon={<Icon icon="rectangle-code" />}
        aria-label="Code block"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isActive={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <IconButtonV2
        icon={<Icon icon="block-quote" />}
        aria-label="Quote block"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        isActive={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <IconButtonV2
        icon={<Icon icon="horizontal-rule" />}
        aria-label="Divider"
        variant="v2-ghost"
        colorScheme="v2-aurora"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
    </Flex>
  );
};

export const RichTextEditor = ({
  text,
  setText,
  isEditable,
  placeHolderText,
  width,
  FlexProps,
}: {
  text: string;
  setText: (nextComment: string) => void;
  isEditable: boolean;
  placeHolderText?: string;
  width?: BoxProps['width'];
  FlexProps?: React.ComponentProps<typeof Flex>;
}) => {
  const editor = useEditor({
    editable: isEditable,
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeHolderText ?? '',
      }),
    ],
    content: text,

    onUpdate: ({ editor: newEditor }) => {
      if (newEditor.isEmpty) {
        // makes it easy to check if text is empty vs checking for empty html tags
        setText('');
      } else {
        setText(newEditor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (text.length === 0 && editor) {
      editor.commands.setContent('');
    }
  }, [editor, text.length]);

  return (
    <Flex
      direction="column"
      borderWidth="1px"
      borderRadius="0.375rem"
      width={width}
      minHeight="5.25rem"
      {...FlexProps}
    >
      {editor && <Toolbar editor={editor} />}
      <Flex cursor="text" flexGrow={1} padding="0.5rem" width="100%">
        <EditorContent
          editor={editor}
          height="100%"
          width="100%"
          style={{ flexGrow: 1 }}
        />
      </Flex>
    </Flex>
  );
};

export const RenderStaticHtml = ({
  textHtml,
  color = 'inherit',
}: {
  textHtml: string;
  color?: string;
}) => {
  const editor = useEditor({
    editable: false,
    extensions: [StarterKit],
    content: textHtml,
  });

  return (
    <EditorContent editor={editor} style={{ color, wordWrap: 'break-word' }} />
  );
};
